
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface SubscriptionData {
  hasActiveSubscription: boolean;
  subscription: {
    id?: string;
    priceId?: string;
    currentPeriodEnd?: string;
    cancelAtPeriodEnd?: boolean;
  } | null;
  isLoading: boolean;
  error: string | null;
}

export const useSubscription = (): SubscriptionData => {
  const { user, session } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<{
    hasActiveSubscription: boolean;
    subscription: SubscriptionData['subscription'];
  }>({
    hasActiveSubscription: false,
    subscription: null
  });
  const { toast } = useToast();

  useEffect(() => {
    const checkSubscription = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      // If user is a super user (Jake's email), set subscription data directly
      if (user.email === 'jake.pimental1@gmail.com') {
        setSubscriptionData({
          hasActiveSubscription: true,
          subscription: {
            id: 'super-user',
            priceId: 'premium',
            currentPeriodEnd: new Date(2099, 11, 31).toISOString(), // Far future date
            cancelAtPeriodEnd: false
          }
        });
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const { data, error } = await supabase.functions.invoke('check-subscription');
        
        if (error) {
          console.error('Error checking subscription:', error);
          setError(error.message);
          
          // Don't show toast for Jake's account even if there's an API error
          if (user.email !== 'jake.pimental1@gmail.com') {
            toast({
              title: "Subscription check failed",
              description: "Unable to verify your subscription status.",
              variant: "destructive"
            });
          }
          return;
        }
        
        setSubscriptionData({
          hasActiveSubscription: data.hasActiveSubscription,
          subscription: data.subscription
        });
      } catch (err) {
        console.error('Unexpected error checking subscription:', err);
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
        
        // Don't show toast for Jake's account even if there's an exception
        if (user.email !== 'jake.pimental1@gmail.com') {
          toast({
            title: "Subscription check failed",
            description: "Unable to verify your subscription status.",
            variant: "destructive"
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkSubscription();
  }, [user, toast]);

  return {
    ...subscriptionData,
    isLoading,
    error
  };
};
